<template>
    <div>
        <ValidationObserver v-slot="{ dirty }" ref="observer">
            <form class="grid grid-cols-2">
                <app-fieldset class="col-span-2 mt-4" :label="$t('commons.infos')">
                    <app-input-text
                        :label="$t('commons.object')"
                        v-model="invoice.object"
                        class="col-span-2 md:col-span-1"
                        :required="true"
                        @blur="save()"
                        ref="objectInput"
                    />
                    <app-input-text
                        :label="$t('commons.code')"
                        v-model="invoice.code"
                        class="col-span-2 md:col-span-1"
                        @blur="save()"
                    />
                    <app-bundle-picker
                        class="col-span-2"
                        v-model="invoice.emitter"
                        :options="bundles"
                        @input="onBundleChange"
                    ></app-bundle-picker>
                    <app-number-input
                        :label="$t('commons.amountTTC')"
                        v-model="invoice.amountTTC"
                        :allowsNegative="true"
                        @blur="onAmountChange"
                        :currency="currencyFormat.currency"
                    />
                </app-fieldset>

                <app-fieldset class="col-span-2 mt-5" :label="$t('commons.dates')">
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="invoice.emissionDate"
                        :label="$t('invoices.emissionDate')"
                        @input="save()"
                    />
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="invoice.invoiceDate"
                        :label="$t('invoices.invoiceDate')"
                        @input="save()"
                    />
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-4" :label="$t('invoices.relatedQuotes')">
                    <invoice-distribution
                        distribution-key="distributionByAmendments"
                        @distributionChanged="onDistributionChanged"
                    />
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-5" :label="$t('invoices.distribution')" v-if="invoice.emitter">
                    <table class="w-full border text-xs col-span-2">
                        <tr class="border-b">
                            <th class="p-2 text-left">{{ $t('invoices.company') }}</th>
                            <th class="p-2 text-right bg-blue-200">{{ $t('commons.amountTTC') }}</th>
                        </tr>
                        <tr class="border-b">
                            <td class="border p-2" style="width: 70%">
                                <span>
                                    <b>[{{ $t('invoices.mainCompany') }}]</b>
                                    {{ invoice.emitter?.company?.name }}
                                </span>
                            </td>
                            <td class="border p-2 text-right bg-blue-200">
                                {{
                                    $n(
                                        invoice.distribution?.find((line) => line.id === invoice.emitterId)
                                            ?.amountTTC || 0,
                                        currencyFormat,
                                    )
                                }}
                            </td>
                        </tr>
                        <tr class="border-b" v-for="subcontractor in subcontractors">
                            <td class="border p-2">
                                <span>{{ subcontractor.name }}</span>
                            </td>
                            <td class="border p-2 text-right bg-blue-200">
                                <app-number-link
                                    :label="$t('commons.amountTTC')"
                                    :show-label="false"
                                    v-model="subcontractor.amountTTC"
                                    @input="saveDistribution()"
                                    :positiveOnly="false"
                                    format="decimal"
                                    class="underline"
                                >
                                    <span class="w-full text-right">
                                        {{ $n(subcontractor.amountTTC, currencyFormat) }}
                                    </span>
                                </app-number-link>
                            </td>
                        </tr>
                    </table>
                </app-fieldset>
                <div class="col-span-2 mt-5">
                    <app-separator
                        :label="$t('invoices.attachment')"
                        icon="icon-file-document-outline"
                        class="my-5 col-span-4"
                    ></app-separator>
                    <div class="flex">
                        <app-upload-button
                            ref="fileInput"
                            icon="icon-paperclip"
                            :label="invoice.url ? $t('invoices.replaceDocument') : $t('invoices.newDocument')"
                            :end-point="`/api/projects/${$route.params.projectId}/invoices/${$route.params.invoiceId}/attachments`"
                            :name="$t('invoices.newDocument')"
                            class="text-sm offline:hidden"
                            @loaded="onLoaded"
                        />
                    </div>
                    <table class="w-full my-5" v-if="invoice.url">
                        <tr class="border-b hover:bg-gray-200">
                            <td class="p-2" style="width: 30px">
                                <a
                                    :href="invoice.url"
                                    :download="invoice.fileName"
                                    aria-label="download attachment"
                                    target="_blank"
                                >
                                    <IconDownload width="16" heigth="16" />
                                </a>
                            </td>
                            <td class="p-2 text-left">
                                <a :href="invoice.url" class="text-sm" aria-label="view attachment" target="_blank">
                                    {{ invoice.fileName }}
                                </a>
                            </td>
                            <td class="p-2" style="width: 30px" v-if="isAdmin">
                                <app-button
                                    size="mini"
                                    variant="danger"
                                    aria-label="remove attachment"
                                    icon="icon-trash-can-outline"
                                    @click="removeAttachment()"
                                />
                            </td>
                        </tr>
                    </table>
                </div>
                <app-save-on-leave :dirty="dirty" :saveFn="() => save(true)"></app-save-on-leave>
            </form>
        </ValidationObserver>
    </div>
</template>

<script>
import { getMapById, reduceSumOn, sanitize, sortBy } from '@/services/sanitize.service';
import { getInvoice, updateInvoice } from './invoices.service';
import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getCompanies } from '@/features/companies/companies.service';
import { queryProject } from '@/features/projects/projects.service';
import { confirm } from '@/features/dialogs/dialogs.service';
import { getInvoiceReviews } from '@/features/invoices/invoiceReviews.service';
import { getAmendments } from '@/features/amendments/amendments.service';
import { getCIEs } from '@/features/cie/CIE.service';
import AppFieldset from '@/components/appFieldset/AppFieldset.vue';
import AppInputText from '@/components/appInputText/AppInputText.vue';
import AppBundlePicker from '@/components/appBundlePicker.vue';
import AppNumberInput from '@/components/appNumberInput/AppNumberInput.vue';
import AppDateInput from '@/components/appDateInput/AppDateInput.vue';
import AppSeparator from '@/components/appSeparator/AppSeparator.vue';
import AppUploadButton from '@/components/app-uploadButton/AppUploadButton.vue';
import IconAlertOutline from '@/icons/IconAlertOutline.vue';
import AppButton from '@/components/appButton/AppButton.vue';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink.vue';
import AppSaveOnLeave from '@/components/AppSaveOnLeave.vue';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker.vue';
import IconDownload from '@/icons/IconDownload.vue';
import { buildSubcontractors, getNewSituationDistribution } from '@/features/statements/statements.service';
import InvoiceDistribution from '@/features/invoices/InvoiceDistribution.vue';

export default {
    components: {
        InvoiceDistribution,
        IconDownload,
        AppMultiPicker,
        AppSaveOnLeave,
        AppNumberLink,
        AppButton,
        IconAlertOutline,
        AppUploadButton,
        AppSeparator,
        AppDateInput,
        AppNumberInput,
        AppBundlePicker,
        AppInputText,
        AppFieldset,
    },
    async created() {
        queryProject(this.$route.params.projectId).then((project) => {
            this.currencyFormat.currency = project.currency;
            this.isAdmin = project.me.allowedFeatures.includes('project_invoices');
            this.project = project;
        });
        this.init();
    },
    watch: {
        $route: function (from, to) {
            if (from.name !== to.name || from.params.invoiceId !== to.params.invoiceId) {
                this.focusDone = false;
            }
            this.init();
        },
    },
    methods: {
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                    getInvoice(this.$route.params.projectId, this.$route.params.invoiceId),
                    getInvoiceReviews(this.$route.params.projectId, this.$route.params.invoiceId),
                    getAmendments(this.$route.params.projectId),
                    getCIEs(this.$route.params.projectId),
                ]).subscribe(async ([bundles, companies, invoice, amendments, cie]) => {
                    const relevantCie = cie.filter((cie) => cie.quoteStatus && cie.quoteStatus === 'validated');
                    const companyMap = getMapById(companies);
                    const mappedBundles = bundles.map((bundle) => ({
                        ...bundle,
                        company: companyMap[bundle.companyId],
                    }));
                    this.bundleMap = getMapById(mappedBundles);
                    this.invoice = {
                        distributionByAmendments: [],
                        ...invoice,
                        emitter: this.bundleMap[invoice.emitterId],
                    };
                    this.bundles = mappedBundles;
                    this.selectedVersion = this.$route.params.invoiceId;
                    this.subcontractors = await buildSubcontractors(
                        this.$route.params.projectId,
                        this.invoice.emitterId,
                        this.invoice.distribution,
                    );
                    this.amendments = sortBy(
                        [
                            ...amendments.filter((amendment) => amendment.quoteStatus === 'validated'),
                            ...relevantCie
                                .filter((cie) => cie.bundleId === invoice.emitterId)
                                .map((cie) => ({ ...cie, type: 'cie' })),
                        ],
                        (amendment) => (amendment.code || 'zzz') + amendment.object,
                    );
                }),
            ];
        },
        computeNewDistribution() {
            const cleanDistribution = this.subcontractors.filter((line) => line && line.amountTTC !== 0);
            const totalDistributionTTC = reduceSumOn(cleanDistribution, 'amountTTC');
            return [
                {
                    id: this.invoice.emitterId,
                    name: this.invoice.emitter?.company?.name,
                    amountTTC: this.invoice.amountTTC - totalDistributionTTC,
                },
                ...cleanDistribution,
            ];
        },
        async saveDistribution() {
            await updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                situationDistribution: this.computeNewSituationDistribution(),
                distribution: this.computeNewDistribution(),
            });
        },
        onDistributionChanged(distributionByAmendments) {
            this.saveDistribution();
            updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                distributionByAmendments,
            });
        },
        async onBundleChange() {
            this.invoice.distribution = [];
            this.subcontractors = await buildSubcontractors(
                this.$route.params.projectId,
                this.invoice.emitterId,
                this.invoice.distribution,
            );
            return updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                emitterId: this.invoice.emitter.id,
                situationDistribution: this.computeNewSituationDistribution(),
                distribution: this.computeNewDistribution(),
            });
        },
        onAmountChange() {
            return updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                amountTTC: this.invoice.amountTTC,
                situationDistribution: this.computeNewSituationDistribution(),
                distribution: this.computeNewDistribution(),
            });
        },
        computeNewSituationDistribution() {
            let cleanDistribution = this.invoice.situationDistribution.filter(
                (line) => line.id !== this.invoice.emitterId && line.amountTTC && line.amountTTC !== 0,
            );
            if (cleanDistribution.length === 0) {
                cleanDistribution = this.subcontractors.filter(
                    (line) => line.id !== this.invoice.emitterId && line.amountTTC && line.amountTTC !== 0,
                );
            }
            return getNewSituationDistribution(this.invoice, cleanDistribution);
        },
        sanitizeInvoice(invoice) {
            return {
                ...sanitize(invoice, ['emitter', 'versions']),
                emitterId: invoice.emitter ? invoice.emitter.id : null,
            };
        },
        async save() {
            const invoiceEntity = this.sanitizeInvoice(this.invoice);
            await updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                ...invoiceEntity,
            });
            if (this.$refs.observer) {
                this.$refs.observer.reset();
            }
        },
        onLoaded(attachment) {
            this.invoice.url = attachment.url;
            this.invoice.fileName = attachment.fileName;
        },
        async removeAttachment() {
            if (await confirm(this.$t('commons.confirmMessage'))) {
                this.invoice.url = null;
                this.invoice.fileName = null;
                await this.save();
            }
        },
    },
    data() {
        return {
            amendments: [],
            chargedAmendments: [],
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            subcontractors: [],
            subscriptions: [],
            bundleMap: {},
            bundles: [],
            invoice: {
                name: '',
                emitter: null,
                versions: [],
                distributionByAmendments: [],
            },
            selectedVersion: null,
            isAdmin: false,
            project: {},
        };
    },
};
</script>
